import Vue, { CreateElement, RenderContext, VNode } from "vue";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { ContentFeed, ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import { getFilesIcon, isMediaFile, isVideoFile } from "@/lib/fileHelpers";
import { $t } from "@/lib/i18nHelpers";
import { MediaFilesController } from "@/lib/MediaFilesController";
import { format } from "@/lib/time";
import BaseChip from "@/components/BaseChip";
import BaseIcon from "@/components/BaseIcon";
import BaseImage from "@/components/BaseImage";
import ContentCover from "@/components/ContentCover";

interface Props {
  postData: ContentFeed | ContentSet;
  small: boolean;
  hideIcon: boolean;
  authUserId: string;
  currentAuthorities: string[];
}

function isContentFeed(f: ContentFeed | ContentSet): f is ContentFeed {
  return "files" in f;
}

export default Vue.extend<Props>({
  functional: true,
  props: {
    postData: {
      type: Object,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    authUserId: {
      type: String,
      default: "",
    },
    currentAuthorities: {
      type: Array,
      default: () => [],
    },
  },
  render: (h: CreateElement, ctx: RenderContext<Props>): VNode => {
    const { postData: feedData, authUserId, currentAuthorities } = ctx.props;
    const coverFile = isContentFeed(feedData) ? feedData.files.find(f => isMediaFile(f)) : feedData.cover;
    const method = (coverFile && isVideoFile(coverFile) && "getVideoThumbnail") || "getImageThumbnail";
    const coverSrc = !coverFile ? "" : MediaFilesController[method](coverFile.urlPart, "480p");

    const overflowElements: VNode[] = [];
    const isAuthor = authUserId === feedData.userId;
    let gradient = true;
    if (!isAuthor) {
      gradient = false;
      // do nothing
    } else if (feedData.processingStatus === "ERROR") {
      // processing error
      overflowElements.push(
        h(
          BaseIcon,
          {
            props: { fontSize: 24 },
            staticClass: "content-feed-cover__gradient-icon mr-1 mb-1",
          },
          ["sci-color-error"]
        ),
        h(BaseIcon, { staticClass: "content-feed-cover__main-icon" }, ["sci-eye-off"])
      );
    } else if (["NEW", "CONVERTING"].indexOf(feedData.processingStatus) > -1) {
      // processing in progress
      overflowElements.push(
        h(
          BaseChip,
          {
            props: { small: true },
            staticClass: "content-feed-cover__gradient-icon link mr-1 mb-1",
          },
          [$t("Processing...")]
        ),
        h(BaseIcon, { staticClass: "content-feed-cover__main-icon" }, ["sci-processing"])
      );
    } else if (feedData.scheduleTime && parseISO(feedData.scheduleTime).getTime() >= Date.now()) {
      // Scheduled
      overflowElements.push(
        h(
          BaseChip,
          {
            props: { small: true },
            staticClass: "content-feed-cover__gradient-icon link mr-1 mb-1",
          },
          [format(parseISO(feedData.scheduleTime), "MM.dd.yy")]
        ),
        h(BaseIcon, { staticClass: "content-feed-cover__main-icon" }, ["sci-watch"])
      );
    } else if (!feedData.publishTime) {
      // not published
      overflowElements.push(h(BaseIcon, { staticClass: "content-feed-cover__main-icon" }, ["sci-eye-off"]));
    } else {
      gradient = false;
    }

    let icon = "sci-file-multiple";
    if (feedData.type === ContentFeedTypeEnum.EVENT) {
      icon = "sci-calendar";
    } else if (feedData.type === ContentFeedTypeEnum.MEDIA && isContentFeed(feedData)) {
      icon = getFilesIcon(feedData.files);
    }
    const topRight = () => {
      if (ctx.props.hideIcon) return;
      return h(BaseIcon, { staticClass: "content-feed-cover__icon" }, [icon]);
    };

    const showLocked =
      isContentFeed(feedData) &&
      checkAuthorities(currentAuthorities, isAuthor ? ["updateMyContentSet"] : ["updateAnyContentSet"])
        ? feedData.subscriptionLevelId !== "0" || feedData.pricePackageId !== "0"
        : false;
    const bottomLeft = () => {
      if (!showLocked) return;
      return h(BaseIcon, { staticClass: "content-feed-cover__icon" }, ["sci-lock"]);
    };

    return h(
      "div",
      {
        ...ctx.data,
        class: { "content-feed-cover": true, "__small": ctx.props.small, "__gradient": gradient, ...ctx.data.class },
        on: { ...ctx.listeners },
      },
      [
        coverSrc &&
          h(BaseImage, {
            class: { "content-feed-cover__image": true },
            props: { src: coverSrc, topRightIcon: !ctx.props.hideIcon, bottomLeftIcon: showLocked },
            scopedSlots: {
              "top-right": topRight,
              "bottom-left": bottomLeft,
            },
          }),
        !coverSrc &&
          h(ContentCover, {
            class: { "content-feed-cover__image": true },
            props: { type: feedData.type },
            scopedSlots: {
              "top-right": topRight,
              "bottom-left": bottomLeft,
            },
          }),
        (gradient && h("div", { staticClass: "content-feed-cover__gradient" }, overflowElements)) || overflowElements,
      ]
    );
  },
});
